import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { XCircle, RefreshCw, HelpCircle, Copy, Check, Bug, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

import { useNoIndex } from '../hooks/useNoIndex';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentFailPage = () => {
  useNoIndex();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [debugData, setDebugData] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const { user } = useAuth();
  
  const conversationId = searchParams.get('conversationId');
  const token = searchParams.get('token');
  const errorCode = searchParams.get('errorCode');
  const errorGroup = searchParams.get('errorGroup');
  const fraudStatus = searchParams.get('fraudStatus');
  const mdStatus = searchParams.get('mdStatus');
  const bankDecline = searchParams.get('bankDecline') === '1';
  const message = searchParams.get('message')?.replace(/\+/g, ' ') || 'Ödeme işlemi başarısız oldu';
  const error = searchParams.get('error');
  
  // Detect bank decline: mdStatus=1 (3D success) but errorGroup=DECLINED
  const isBankDecline = bankDecline || (mdStatus === '1' && errorGroup === 'DECLINED');

  // Fetch debug data from retrieve endpoint (admin only)
  useEffect(() => {
    const fetchDebugData = async () => {
      if (user?.role !== 'admin') return;
      
      const tokenToUse = token || localStorage.getItem('lastPaymentToken');
      if (!tokenToUse) return;
      
      setDebugLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/payment/retrieve?token=${tokenToUse}`);
        const data = await response.json();
        setDebugData(data);
      } catch (err) {
        console.error('Debug fetch error:', err);
      } finally {
        setDebugLoading(false);
      }
    };
    
    fetchDebugData();
  }, [token, user]);

  const getErrorDescription = (code) => {
    const errorMap = {
      '10051': 'Kart limiti yetersiz',
      '10005': 'İşlem onaylanmadı',
      '10012': 'Geçersiz kart numarası',
      '10057': 'Kart sahibi işlem yapamaz',
      '10058': 'Terminale işlem yasağı',
      '10034': 'Sahte kart şüphesi',
      '10208': 'Üye işyeri kategori kodu hatalı (MCC)',
      '10220': 'Ödeme alınamadı (Banka reddi)',
      'token_missing': 'Ödeme token bulunamadı',
      'intent_not_found': 'Ödeme kaydı bulunamadı',
      'server_error': 'Sunucu hatası oluştu'
    };
    return errorMap[code] || null;
  };

  const errorDescription = getErrorDescription(errorCode || error);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Kopyalandı!');
    setTimeout(() => setCopied(false), 2000);
  };

  const debugInfo = `ConversationId: ${conversationId || 'N/A'}\nToken: ${token || 'N/A'}\nErrorCode: ${errorCode || error || 'N/A'}\nErrorGroup: ${errorGroup || 'N/A'}\nFraudStatus: ${fraudStatus || 'N/A'}\nMdStatus: ${mdStatus || 'N/A'}\nMessage: ${message}`;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <XCircle className="w-20 h-20 text-red-500" />
          </div>
          <CardTitle className="text-2xl text-red-600">
            Ödeme Başarısız
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-6">
          <div className="space-y-2">
            <p className="text-muted-foreground">{message}</p>
            {isBankDecline ? (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 mt-2">
                <p className="text-sm text-amber-600 font-medium">
                  🏦 Banka Reddi Tespit Edildi
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  3D Secure doğrulaması başarılı oldu ancak bankanız ödemeyi reddetti. 
                  Bu genellikle limit, günlük işlem sınırı veya banka güvenlik politikasından kaynaklanır.
                </p>
              </div>
            ) : errorDescription ? (
              <p className="text-sm text-red-500">{errorDescription}</p>
            ) : null}
          </div>
          
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 text-sm space-y-2 text-left">
            {conversationId && (
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">İşlem No:</span>
                <span className="font-mono text-xs">{conversationId}</span>
              </div>
            )}
            {(errorCode || error) && (
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Hata Kodu:</span>
                <span className="font-mono text-xs text-red-500">{errorCode || error}</span>
              </div>
            )}
            {errorGroup && (
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Hata Grubu:</span>
                <span className="font-mono text-xs text-red-500">{errorGroup}</span>
              </div>
            )}
            {mdStatus && (
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">3D Status:</span>
                <span className={`font-mono text-xs ${mdStatus === '1' ? 'text-green-500' : 'text-red-500'}`}>
                  {mdStatus === '1' ? '✓ Başarılı' : mdStatus}
                </span>
              </div>
            )}
            <div className="pt-2 border-t border-red-500/20 mt-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-xs text-muted-foreground hover:text-foreground"
                onClick={() => copyToClipboard(debugInfo)}
              >
                {copied ? (
                  <Check className="w-3 h-3 mr-1" />
                ) : (
                  <Copy className="w-3 h-3 mr-1" />
                )}
                Hata bilgilerini kopyala
              </Button>
            </div>
          </div>
          
          {/* Admin Debug Box */}
          {user?.role === 'admin' && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 text-xs text-left">
              <div className="flex items-center gap-2 mb-2 text-amber-600 font-medium">
                <Bug className="w-4 h-4" />
                Admin Debug (iyzico Raw Response)
              </div>
              {debugLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Yükleniyor...
                </div>
              ) : debugData ? (
                <div className="space-y-1 font-mono overflow-x-auto max-h-96 overflow-y-auto">
                  <div className="font-semibold text-amber-600 mb-2">-- Payment Status --</div>
                  <div><span className="text-muted-foreground">paymentStatus:</span> <span className={debugData.key_fields?.paymentStatus === 'SUCCESS' ? 'text-green-500' : 'text-red-500'}>{debugData.key_fields?.paymentStatus || 'N/A'}</span></div>
                  <div><span className="text-muted-foreground">fraudStatus:</span> {debugData.key_fields?.fraudStatus || 'N/A'}</div>
                  <div><span className="text-muted-foreground">mdStatus:</span> <span className={debugData.key_fields?.mdStatus === 1 ? 'text-green-500' : 'text-amber-500'}>{debugData.key_fields?.mdStatus || 'N/A'}</span></div>
                  
                  <div className="font-semibold text-amber-600 mt-3 mb-2">-- Error Info --</div>
                  <div><span className="text-muted-foreground">errorCode:</span> <span className="text-red-500">{debugData.key_fields?.errorCode || 'N/A'}</span></div>
                  <div><span className="text-muted-foreground">errorMessage:</span> {debugData.key_fields?.errorMessage || 'N/A'}</div>
                  <div><span className="text-muted-foreground">errorGroup:</span> <span className="text-red-500">{debugData.iyzico_raw_response?.errorGroup || 'N/A'}</span></div>
                  
                  <div className="font-semibold text-amber-600 mt-3 mb-2">-- Transaction IDs --</div>
                  <div><span className="text-muted-foreground">paymentId:</span> {debugData.key_fields?.paymentId || 'N/A'}</div>
                  <div><span className="text-muted-foreground">conversationId:</span> {debugData.key_fields?.conversationId || 'N/A'}</div>
                  <div><span className="text-muted-foreground">basketId:</span> {debugData.iyzico_raw_response?.basketId || 'N/A'}</div>
                  
                  <div className="font-semibold text-amber-600 mt-3 mb-2">-- Amount Info --</div>
                  <div><span className="text-muted-foreground">price:</span> {debugData.key_fields?.price || 'N/A'}</div>
                  <div><span className="text-muted-foreground">paidPrice:</span> {debugData.key_fields?.paidPrice || 'N/A'}</div>
                  <div><span className="text-muted-foreground">currency:</span> {debugData.iyzico_raw_response?.currency || 'N/A'}</div>
                  <div><span className="text-muted-foreground">installment:</span> {debugData.iyzico_raw_response?.installment || 'N/A'}</div>
                  
                  <div className="font-semibold text-amber-600 mt-3 mb-2">-- Card Info --</div>
                  <div><span className="text-muted-foreground">cardFamily:</span> {debugData.iyzico_raw_response?.cardFamily || 'N/A'}</div>
                  <div><span className="text-muted-foreground">cardAssociation:</span> {debugData.iyzico_raw_response?.cardAssociation || 'N/A'}</div>
                  <div><span className="text-muted-foreground">cardType:</span> {debugData.iyzico_raw_response?.cardType || 'N/A'}</div>
                  <div><span className="text-muted-foreground">binNumber:</span> {debugData.iyzico_raw_response?.binNumber || 'N/A'}</div>
                  <div><span className="text-muted-foreground">lastFourDigits:</span> {debugData.iyzico_raw_response?.lastFourDigits || 'N/A'}</div>
                  
                  <div className="font-semibold text-amber-600 mt-3 mb-2">-- Auth Info --</div>
                  <div><span className="text-muted-foreground">authCode:</span> {debugData.iyzico_raw_response?.authCode || 'N/A'}</div>
                  <div><span className="text-muted-foreground">hostReference:</span> {debugData.iyzico_raw_response?.hostReference || 'N/A'}</div>
                  
                  {debugData.iyzico_raw_response?.itemTransactions && debugData.iyzico_raw_response.itemTransactions.length > 0 && (
                    <>
                      <div className="font-semibold text-amber-600 mt-3 mb-2">-- Item Transactions --</div>
                      {debugData.iyzico_raw_response.itemTransactions.map((tx, i) => (
                        <div key={i} className="pl-2 border-l-2 border-amber-500/30 mb-2">
                          <div><span className="text-muted-foreground">txId:</span> {tx.paymentTransactionId || 'N/A'}</div>
                          <div><span className="text-muted-foreground">itemId:</span> {tx.itemId || 'N/A'}</div>
                          <div><span className="text-muted-foreground">price:</span> {tx.price || 'N/A'}</div>
                          <div><span className="text-muted-foreground">status:</span> {tx.transactionStatus || 'N/A'}</div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              ) : (
                <div className="text-muted-foreground">Debug verisi yüklenemedi</div>
              )}
            </div>
          )}
          
          <div className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-3">
            <p className="flex items-center gap-1 justify-center">
              <HelpCircle className="w-3 h-3" />
              Ödeme işleminiz gerçekleşmediyse kartınızdan kesinti yapılmaz.
            </p>
          </div>
          
          <div className="pt-4 space-y-3">
            <Button 
              onClick={() => navigate('/pricing')} 
              className="w-full"
              data-testid="retry-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Tekrar Dene
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/contact')} 
              className="w-full"
              data-testid="support-btn"
            >
              Destek Al
            </Button>
            <Button 
              variant="ghost"
              onClick={() => navigate('/dashboard')} 
              className="w-full text-muted-foreground"
              data-testid="goto-dashboard-btn"
            >
              Dashboard'a Dön
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentFailPage;
